import mixpanel from 'mixpanel-browser';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { setGlobalTrackFunction } from '../utils/analytics';
import { UserContext } from './UserContext';

interface AnalyticsContextValue {
  track: (eventName: string, properties?: Record<string, any>) => void;
  trackPageView: (pageName: string, properties?: Record<string, any>) => void;
  setUserProperty: (property: string, value: any) => void;
  setUserProperties: (properties: Record<string, any>) => void;
  identify: (userId: string, properties?: Record<string, any>) => void;
  reset: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(
  undefined,
);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const { selectedAccount } = useContext(UserContext);
  const isProduction = import.meta.env.MODE === 'production';

  // Base properties that should be included with every event
  const baseProperties = useMemo(() => {
    const props: Record<string, any> = {
      url: window.location.href,
      pathname: window.location.pathname,
      environment: import.meta.env.MODE,
    };

    // Auto-include account_id if available
    if (selectedAccount?.id) {
      props.account_id = selectedAccount.id;
      props.account_name = selectedAccount.data().name;
    }

    return props;
  }, [selectedAccount]);

  // Track page views automatically when selectedAccount changes
  useEffect(() => {
    if (selectedAccount?.id) {
      try {
        mixpanel.register({
          account_id: selectedAccount.id,
          account_name: selectedAccount.data().name,
        });
      } catch (error) {
        console.error(
          '[ANALYTICS] Failed to register account properties:',
          error,
        );
      }
    }
  }, [selectedAccount]);

  const track = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      // Always log in development
      if (!isProduction) {
        console.debug('[ANALYTICS]', eventName, {
          ...baseProperties,
          ...properties,
        });
      }

      // Only send to Mixpanel in production or if explicitly enabled
      if (isProduction || import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
        try {
          mixpanel.track(eventName, {
            ...baseProperties,
            ...properties,
          });
        } catch (error) {
          console.error('[ANALYTICS] Failed to track event:', eventName, error);
        }
      }
    },
    [baseProperties, isProduction],
  );

  // Set the global track function for backward compatibility
  useEffect(() => {
    setGlobalTrackFunction(track);
    return () => {
      setGlobalTrackFunction(() => {});
    };
  }, [track]);

  const trackPageView = useCallback(
    (pageName: string, properties?: Record<string, any>) => {
      track('page_view', {
        page_name: pageName,
        ...properties,
      });
    },
    [track],
  );

  const setUserProperty = useCallback(
    (property: string, value: any) => {
      if (!isProduction && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
        console.debug('[ANALYTICS] Set user property:', property, value);
        return;
      }

      try {
        mixpanel.people.set({ [property]: value });
      } catch (error) {
        console.error(
          '[ANALYTICS] Failed to set user property:',
          property,
          error,
        );
      }
    },
    [isProduction],
  );

  const setUserProperties = useCallback(
    (properties: Record<string, any>) => {
      if (!isProduction && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
        console.debug('[ANALYTICS] Set user properties:', properties);
        return;
      }

      try {
        mixpanel.people.set(properties);
      } catch (error) {
        console.error('[ANALYTICS] Failed to set user properties:', error);
      }
    },
    [isProduction],
  );

  const identify = useCallback(
    (userId: string, properties?: Record<string, any>) => {
      if (!isProduction && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
        console.debug('[ANALYTICS] Identify:', userId, properties);
        return;
      }

      try {
        mixpanel.identify(userId);
        if (properties) {
          mixpanel.people.set(properties);
        }
      } catch (error) {
        console.error('[ANALYTICS] Failed to identify user:', userId, error);
      }
    },
    [isProduction],
  );

  const reset = useCallback(() => {
    if (!isProduction && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV) {
      console.debug('[ANALYTICS] Reset');
      return;
    }

    try {
      mixpanel.reset();
    } catch (error) {
      console.error('[ANALYTICS] Failed to reset:', error);
    }
  }, [isProduction]);

  const value = useMemo(
    () => ({
      track,
      trackPageView,
      setUserProperty,
      setUserProperties,
      identify,
      reset,
    }),
    [track, trackPageView, setUserProperty, setUserProperties, identify, reset],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

// Backward compatibility export
export const useTrack = () => {
  const { track } = useAnalytics();
  return track;
};
